/**
 * File picker para cargar carpetas de música.
 *
 * El usuario pulsa "+ CARPETA" → se dispara un input file con
 * `webkitdirectory` → llamamos a `onFolderPicked()` con la lista de
 * archivos elegidos.
 *
 * Comportamiento:
 *  - Filtra archivos de audio por extensión o MIME.
 *  - Crea blob URLs para cada archivo (vivas mientras dure la sesión).
 *  - Reemplaza la selección anterior y la persiste en IndexedDB.
 *  - Llama a `reconcileGhosts()` para re-asociar tracks fantasma de las
 *    playlists del usuario con los archivos reales por nombre.
 *  - Persiste y re-renderiza.
 *
 * Nota: la default playlist NO se rellena desde aquí — es server-backed
 * (ver server-music.ts). Las tracks de carpeta van a playlists del usuario.
 */

import { isAudioFile, stripExtension, userTrackId } from './helpers.js';
import { idbClearTracks, idbPutTracks } from './idb-store.js';
import { savePlaylists } from './persistence.js';
import { getCallbacks } from './controls.js';
import { renderFileList, renderFolderList, renderPlaylists } from './render.js';
import { setUserTracks, state } from './state.js';
import type { Track } from './types.js';

/**
 * Re-asigna IDs ghost de las playlists a IDs reales recién creados, cuando
 * el nombre del archivo (sin extensión) coincide. Esto ocurre cuando el
 * usuario re-elige la carpeta tras un reload: los ghosts se "rellenan"
 * con sus blob URLs reales.
 */
export function reconcileGhosts(): void {
  const byName = new Map<string, string>();
  for (const t of state.userTracks) {
    byName.set(stripExtension(t.name).toLowerCase(), t.id);
  }
  for (const pl of state.playlists) {
    if (!pl.userTrackMeta) continue;
    pl.tracks = (pl.tracks ?? []).map((tid) => {
      // Si el id ya es de un track real, no tocar
      if (state.userTracks.some((t) => t.id === tid)) return tid;
      // Si es ghost, buscar su nombre en userTrackMeta y rebuscar por nombre
      const meta = pl.userTrackMeta?.find((x) => x.id === tid);
      if (meta) {
        const realId = byName.get(stripExtension(meta.name).toLowerCase());
        if (realId) return realId;
      }
      return tid;
    });
  }
}

/**
 * Handler del input file. Recibe el evento de `change` con la selección
 * de archivos del usuario.
 */
export function onFolderPicked(e: Event): void {
  const input = e.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  const audios = files.filter(isAudioFile);

  if (audios.length === 0) {
    alert('No se han encontrado archivos de audio en esa carpeta.');
    return;
  }

  // Revocar las blob URLs de la selección anterior para no fugar memoria.
  for (const t of state.userTracks) {
    try {
      URL.revokeObjectURL(t.file);
    } catch {
      /* ignore */
    }
  }

  // Reemplazar todo el contenido de userTracks con la nueva selección
  const idForFile = (f: File): string => userTrackId(f.name, f.size);
  const newTracks: Track[] = audios.map((f) => ({
    id: idForFile(f),
    name: f.name,
    file: URL.createObjectURL(f),
    isUser: true,
  }));
  setUserTracks(newTracks);

  // Persistir blobs en IndexedDB (local al navegador) para que sigan
  // disponibles al re-montar la radio sin tener que re-elegir carpeta.
  // Reemplazamos todo: limpiamos y volvemos a guardar la selección
  // entera. Es coherente con el comportamiento UI ("esta carpeta
  // sustituye a la anterior") y simple — no se gestiona "merge".
  void (async () => {
    await idbClearTracks();
    await idbPutTracks(
      audios.map((f) => ({ id: idForFile(f), name: f.name, blob: f })),
    );
  })();

  reconcileGhosts();

  savePlaylists();
  renderFileList(getCallbacks());
  renderFolderList(getCallbacks());
  renderPlaylists(getCallbacks());

  // Limpiar el input para que se pueda elegir la misma carpeta de nuevo
  input.value = '';
}

/**
 * Configura el listener del file-picker y del botón "+ CARPETA" que lo
 * dispara. Idempotente: comprueba dataset flag.
 */
export function initFolderPicker(): void {
  const pickBtn = document.getElementById('r-pick-folder');
  const folderInput = document.getElementById('r-folder-input') as HTMLInputElement | null;
  if (!pickBtn || !folderInput) return;

  if (pickBtn.dataset.bound === '1') return;
  pickBtn.dataset.bound = '1';

  pickBtn.addEventListener('click', () => folderInput.click());
  folderInput.addEventListener('change', onFolderPicked);
}
